import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const SUCCESS_MESSAGE_DURATION = 2500;

class BogoPairSelector extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bogo-form]');
    this.selects = Array.from(this.querySelectorAll('[data-bogo-select]'));
    this.button = this.querySelector('[data-bogo-submit]');
    this.buttonText = this.querySelector('[data-bogo-button-text]');
    this.status = this.querySelector('[data-bogo-status]');
    this.stock = this.querySelector('[data-bogo-stock]');
    this.stockText = this.querySelector('[data-bogo-stock-text]');

    if (!this.form || this.selects.length !== 2 || !this.button || !this.buttonText) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.selects.forEach((select) => select.addEventListener('change', this.handleSelectionChange, { signal }));
    this.form.addEventListener('submit', this.handleSubmit, { signal });
    this.updateOffer();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.resetTimeout) clearTimeout(this.resetTimeout);
  }

  handleSelectionChange = (event) => {
    const select = event.currentTarget;
    const pair = select.dataset.bogoSelect;
    const image = this.querySelector(`[data-bogo-image='${pair}'] img`);
    const selected = select.selectedOptions[0];

    if (image && selected?.dataset.image) {
      image.removeAttribute('srcset');
      image.src = selected.dataset.image;
    }
    if (image && selected?.textContent) image.alt = selected.textContent.trim();

    this.updateOffer();
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.button.disabled) return;

    const selections = this.selects.map((select) => select.selectedOptions[0]);
    if (selections.some((option) => !option || option.dataset.available !== 'true')) {
      this.updateOffer();
      return;
    }

    const quantities = new Map();
    selections.forEach((option) => quantities.set(option.value, (quantities.get(option.value) || 0) + 1));
    const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
    const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));

    this.setLoading(true);
    this.setStatus('');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items,
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorLabel);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      if (!cartResponse.ok || cart.status) {
        throw new Error(cart.description || cart.message || this.dataset.errorLabel);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: result.sections,
          items: cart.items,
          itemCount: 2,
          source: 'bogo-pair-selector',
          sourceId: this.id,
          productId: this.dataset.productId,
          didError: false,
        },
      });

      this.buttonText.textContent = this.dataset.addedLabel;
      this.setStatus(this.dataset.addedLabel);
      this.resetTimeout = setTimeout(() => {
        this.buttonText.textContent = this.dataset.buttonLabel;
        this.setStatus('');
      }, SUCCESS_MESSAGE_DURATION);
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error && error.message ? error.message : this.dataset.errorLabel;
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );
      this.setStatus(message);
      this.buttonText.textContent = this.dataset.buttonLabel;
    } finally {
      this.setLoading(false);
    }
  };

  updateOffer() {
    const options = this.selects.map((select) => select.selectedOptions[0]);
    const available = options.every((option) => option?.dataset.available === 'true');
    const prices = options.map((option) => Number(option?.dataset.price || 0));
    const total = prices.reduce((sum, price) => sum + price, 0);
    const payable = Math.max(...prices);
    const savings = Math.min(...prices);

    this.updateMoney('[data-bogo-payable]', payable);
    this.updateMoney('[data-bogo-total]', total);
    this.updateMoney('[data-bogo-savings]', savings);

    this.button.disabled = !available;
    this.stock?.setAttribute('data-available', String(available));
    if (this.stockText) {
      this.stockText.textContent = available ? this.dataset.availableLabel : this.dataset.unavailableLabel;
    }

    this.refreshCurrencyConverter();
  }

  updateMoney(selector, amount) {
    const slot = this.querySelector(selector);
    if (!slot) return;

    const money = document.createElement('span');
    money.className = 'money';
    money.textContent = formatMoney(amount, this.dataset.moneyFormat, this.dataset.currency);
    slot.replaceChildren(money);
  }

  refreshCurrencyConverter() {
    const refresh = () => requestAnimationFrame(() => window.bucksCC?.reConvert?.());
    if (typeof window.bucksCC?.reConvert === 'function') refresh();
    else window.addEventListener('BUCKSCC-LOADED', refresh, { once: true });
  }

  getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));
    if (isLoading) this.buttonText.textContent = this.dataset.addingLabel;
    else this.updateOffer();
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('bogo-pair-selector')) {
  customElements.define('bogo-pair-selector', BogoPairSelector);
}
